import * as React from 'react';
import { motion, Variants } from 'framer-motion';
import { observer } from 'mobx-react';

import {
  MANAGER_SEGMENTS,
  ManagerSegment
} from 'shared/entities/managerSegment';
import { useTypedTranslation } from 'shared/entities/localization';
import { useManagerSegmentStore } from 'stores/managerSegmentStore';
import Typography from 'shared/newComponents/Typography';
import {
  TypographyColor,
  TypographySize,
  TypographyType
} from 'shared/newEntities/components/Typography';
import Collapsed from 'shared/newComponents/Collapsed';
import RadioGroup from 'shared/newComponents/form/RadioGroup';

import OtherTextField from '../OtherTextField';
import StepFooter from '../StepFooter';
import cardStyles from '../OptionCard.modules.scss';

import './SegmentStep.modules.scss';

type Props = {
  chunkVariants: Variants;
  onContinue: () => void;
};

const SegmentStep: React.FC<Props> = ({ chunkVariants, onContinue }: Props) => {
  const store = useManagerSegmentStore();
  const { t } = useTypedTranslation('app');

  return (
    <>
      <motion.div variants={chunkVariants}>
        <Typography
          fontType={TypographyType.text}
          fontSize={TypographySize.s}
          fontColor={TypographyColor.secondary}
          styleName="segment-step__description"
        >
          {t('managerSegment.segment.description')}
        </Typography>
      </motion.div>

      <motion.div variants={chunkVariants} styleName="segment-step__scroll">
        <RadioGroup
          name="manager-segment"
          value={store.segment.value}
          options={MANAGER_SEGMENTS.map((segment) => ({
            id: segment,
            label: t(`managerSegment.segments.${segment}`)
          }))}
          onChange={store.selectSegment}
          disabled={store.isSubmitting}
          ariaLabel={t('managerSegment.segment.title')}
          itemClassName={cardStyles['option-card']}
        />

        <Collapsed opened={store.segment.value === ManagerSegment.other}>
          <div styleName="segment-step__other">
            <OtherTextField
              id="manager-segment-other"
              value={store.segmentOtherText.value}
              error={store.segmentOtherText.error}
              disabled={
                store.isSubmitting ||
                store.segment.value !== ManagerSegment.other
              }
              label={t('managerSegment.other.label')}
              placeholder={t('managerSegment.other.placeholder')}
              onChange={store.changeSegmentOtherText}
            />
          </div>
        </Collapsed>
      </motion.div>

      <StepFooter
        chunkVariants={chunkVariants}
        primaryLabel={t('managerSegment.actions.continue')}
        primaryDisabled={!store.canContinue || store.isSubmitting}
        primaryLoading={false}
        skipLoading={store.skipStage.isLoading}
        disabled={store.isSubmitting}
        onPrimary={onContinue}
        onSkip={store.skip}
      />
    </>
  );
};

export default observer(SegmentStep);
